import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, Heart } from 'lucide-react';
import LanguageToggle from './LanguageToggle';

const Navbar = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdown(null); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => { setIsMenuOpen(false); setOpenDropdown(null); }, [location]);

  const navItems = [
    {
      label: isZh ? '产品' : 'Products',
      sections: [
        { heading: isZh ? 'ADU 户型' : 'ADUs', items: [
          { to: '/floor-plans', label: isZh ? 'ADU 户型方案' : 'ADU Floor Plans' },
          { to: '/packages', label: isZh ? 'ADU 套餐' : 'ADU Packages' },
        ]},
        { heading: isZh ? '住宅' : 'Homes', items: [
          { to: '/single-family-homes', label: isZh ? '阿尔塔迪纳 & 太平洋帕利塞德' : 'Altadena & Pacific Palisades' },
          { to: '/sf-homes', label: isZh ? '独栋住宅' : 'Single-Family Homes' },
        ]}
      ]
    },
    {
      label: isZh ? '服务' : 'Services',
      sections: [
        { items: [
          { to: '/design', label: isZh ? '设计房屋' : 'Design MyHome' },
          { to: '/turnkey-services', label: isZh ? '一站式全包服务' : 'Our Turnkey Services' },
          { to: '/services#design', label: isZh ? '建筑设计' : 'Architectural Design', isHash: true },
          { to: '/services#construction', label: isZh ? '建筑施工' : 'Construction' , isHash: true },
          { to: '/services#financing', label: isZh ? '建筑贷款' : 'Construction Financing', isHash: true },
        ]}
      ]
    },
    {
      label: isZh ? '了解更多' : 'Discover More',
      sections: [
        { items: [
          { to: '/#process', label: isZh ? 'ALIGCUS 服务流程' : 'How ALIGCUS Works', isHash: true },
          { to: '/about', label: isZh ? '关于我们' : 'About Us' },
          { to: '/agent-partnership', label: isZh ? '经纪人合作' : 'Agent Partnership' },
          { to: '/news', label: isZh ? '新闻中心' : 'News' },
          { to: '/blog', label: isZh ? '博客文章' : 'Blog' },
        ]}
      ]
    },
    {
      label: isZh ? '项目案例' : 'Portfolio',
      to: '/portfolio'
    },
    {
      label: isZh ? '活动' : 'Events',
      sections: [
        { items: [
          { to: '/events/south', label: isZh ? '南加州活动' : 'Southern California' },
          { to: '/events/north', label: isZh ? '北加州活动' : 'Northern California' },
        ]}
      ]
    },
    {
      label: isZh ? 'ADU 法规' : 'ADU Regulations',
      to: '/adu-regulations'
    },
    {
      label: isZh ? '预审批方案' : 'Pre-Approved',
      labelMobile: isZh ? '预审批方案' : 'Pre-Approved',
      to: '/pre-approved'
    },
    {
      label: isZh ? '材料选购' : 'Order Material Selection',
      to: '/shop'
    }
  ];

  const renderDropdown = (item, idx) => (
    <div key={idx} className="relative">
      <button
        onClick={() => setOpenDropdown(openDropdown === idx ? null : idx)}
        className="flex items-center gap-1 text-gray-700 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-all font-semibold text-base"
      >
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === idx ? 'rotate-180' : ''}`} />
      </button>
      {openDropdown === idx && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-3 z-50">
          {item.sections.map((section, si) => (
            <div key={si}>
              {section.heading && (
                <div className="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider">{section.heading}</div>
              )}
              {section.items.map((link, li) => (
                link.isHash ? (
                  <a key={li} href={link.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">{link.label}</a>
                ) : (
                  <Link key={li} to={link.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">{link.label}</Link>
                )
              ))}
              {si < item.sections.length - 1 && <div className="my-2 border-t border-gray-100" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Announcement Banner - Rebuilding SoCal */}
      <div className="bg-gray-900 text-white py-2 print:hidden" data-testid="announcement-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
          <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500 fill-red-500 flex-shrink-0" />
          <span className="text-gray-300 line-clamp-1">
            {isZh ? 'ALIGCUS 正在为南加州火灾受灾家庭重建家园。' : 'ALIGCUS is rebuilding homes for those impacted by the SoCal fires.'}
          </span>
          <Link to="/socal-rebuilds" className="font-semibold text-white border border-white/30 px-2 sm:px-3 py-0.5 sm:py-1 rounded hover:bg-white/10 transition-colors whitespace-nowrap text-xs sm:text-sm flex-shrink-0">
            {isZh ? '免费咨询' : 'Request a free consultation'}
          </Link>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`sticky top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`} data-testid="main-navbar">
        <div className="w-full px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img src="https://customer-assets.emergentagent.com/job_9ca81a2a-0a4d-4b76-b02e-3997c1e64651/artifacts/ypha2bz9_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20260309130435_160_131.png" alt="ALIGCUS" className="h-10 sm:h-12 w-auto" />
            </Link>

            {/* Desktop - centered nav */}
            <div className="hidden lg:flex items-center gap-3 flex-1 justify-center whitespace-nowrap" ref={dropdownRef}>
              <Link to="/" className="text-gray-700 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-all font-semibold text-base">{isZh ? '首页' : 'HOME'}</Link>
              {navItems.map((item, idx) =>
                item.sections ? renderDropdown(item, idx) : (
                  item.isHash ? (
                    <a key={idx} href={item.to} className="text-gray-700 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-all font-semibold text-base">{item.label}</a>
                  ) : (
                    <Link key={idx} to={item.to} className="text-gray-700 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-all font-semibold text-base">{item.label}</Link>
                  )
                )
              )}
            </div>

            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <LanguageToggle />
              <Link to="/profile" className="text-gray-500 hover:text-red-600 transition-colors" title={isZh ? '个人中心' : 'My Account'}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              </Link>
            </div>

            {/* Mobile */}
            <div className="lg:hidden flex items-center gap-3">
              <LanguageToggle />
              <Link to="/profile" className="text-gray-500 hover:text-red-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              </Link>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-700 hover:text-red-600">
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t pt-4 space-y-1">
              {navItems.map((item, idx) =>
                item.sections ? (
                  <div key={idx}>
                    <div className="px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">{item.label}</div>
                    {item.sections.map((section, si) => (
                      <div key={si}>
                        {section.heading && <div className="px-5 py-1 text-xs text-red-600 font-semibold">{section.heading}</div>}
                        {section.items.map((link, li) =>
                          link.isHash ? (
                            <a key={li} href={link.to} className="block px-5 py-2 text-gray-700 hover:text-red-600">{link.label}</a>
                          ) : (
                            <Link key={li} to={link.to} className="block px-5 py-2 text-gray-700 hover:text-red-600">{link.label}</Link>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  item.isHash ? (
                    <a key={idx} href={item.to} className="block px-3 py-2 text-gray-700 hover:text-red-600">{item.label}</a>
                  ) : (
                    <Link key={idx} to={item.to} className="block px-3 py-2 text-gray-700 hover:text-red-600">{item.label}</Link>
                  )
                )
              )}
              <Link to="/estimate" className="block mt-3 px-3">
              </Link>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
